import React from 'react';

import { useIntl } from 'react-intl';

import { toolbarInsertBlockMessages } from '@atlaskit/editor-common/messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { BlockMenuItemComponentProps } from '@atlaskit/editor-plugin-block-menu/blockMenuPluginType';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';
import LozengeIcon from '@atlaskit/icon-lab/core/lozenge';

import { createStatusNode } from '../utils/createStatusNode';
import { pluginKey } from '../pm-plugins/plugin-key';
import type { StatusPlugin } from '../statusPluginType';

type Props = {
	api: ExtractInjectionAPI<StatusPlugin> | undefined;
	isSuggested?: boolean;
};

const StatusBlockMenuItem = ({ api, isSuggested }: Props) => {
	const { formatMessage } = useIntl();

	const handleClick = () => {
		api?.core.actions.execute(({ tr }) => {
			const command = api?.blockMenu?.commands.transformInlineNode({
				buildInlineNode: (source) =>
					createStatusNode(tr.doc.type.schema, { text: source.textContent.trim() }),
				isSuggested,
				targetTypeName: 'status',
			});
			const nextTr = command?.({ tr });
			if (!nextTr?.docChanged) {
				return null;
			}

			return nextTr.setMeta(pluginKey, {
				focusStatusInput: true,
				isNew: true,
				showStatusPickerAt: nextTr.selection.from,
			});
		});
	};

	return (
		<ToolbarDropdownItem onClick={handleClick} elemBefore={<LozengeIcon label="" size="small" />}>
			{formatMessage(toolbarInsertBlockMessages.status)}
		</ToolbarDropdownItem>
	);
};

export const createStatusBlockMenuItem = (api: ExtractInjectionAPI<StatusPlugin> | undefined) => {
	return ({ isSuggested }: BlockMenuItemComponentProps = {}): React.JSX.Element => (
		<StatusBlockMenuItem api={api} isSuggested={isSuggested} />
	);
};
